import { useContext, useState } from "react";

import { CartContext } from "../../contexts/CartContext";
import FoundryLink from "../../components/FoundryLink";
import FoundryPhysicalProduct from "../../lib/foundry_client/models/FoundryPhysicalProduct";
import { PhysicalProductState } from "../../lib/models/PhysicalProducts";
import ResponsiveImage from "../ResponsiveImage";
import Routes from "../../routes";
import TrashEmptySvg from "../../svg/trash-empty.svg";
import { formatMoneyShort } from "../../util/strings";
import { getUpdatedPhysicalProducts } from "../../lib/cart/CartDetails";
import { savePhysicalProducts } from "../../lib/cart/CartStorage";
import useQuantitySelect from "../../hooks/useQuantitySelect";

interface PhysicalProductSectionProps {
  product: PhysicalProductState;
  productDetails: FoundryPhysicalProduct;
  // The total discount applied across this product's quantity, if any.
  discountCents?: number;
  disableEdits?: boolean;
}

export default function PhysicalProductSection({
  product,
  productDetails,
  discountCents,
  disableEdits,
}: PhysicalProductSectionProps) {
  const { cartDetails, propagateCartDetails } = useContext(CartContext);
  const [visible, setVisible] = useState(true);

  const price = productDetails.priceCents * product.quantity;

  const [quantitySelector] = useQuantitySelect(
    productDetails.maxQuantity,
    product.quantity,
    (n: number) => {
      if (disableEdits) return;

      const newState = getUpdatedPhysicalProducts(
        cartDetails?.physicalProducts,
        product.contentfulId,
        { quantity: n }
      );
      savePhysicalProducts(newState);
      propagateCartDetails();
    },
    disableEdits
  );

  const handleRemoveProduct = (product: PhysicalProductState): void => {
    const newState = getUpdatedPhysicalProducts(
      cartDetails?.physicalProducts,
      product.contentfulId,
      { quantity: 0 }
    );

    setVisible(false);

    setTimeout(() => {
      savePhysicalProducts(newState);
      propagateCartDetails();
    }, 500);
  };

  return (
    <>
      <div
        className={`flex flex-row justify-between ${
          visible ? "opacity-100" : "opacity-0"
        } transition-opacity`}
      >
        <div className="flex flex-row gap-8">
          <div className="w-24">
            <FoundryLink href={Routes.shop.product(productDetails.slug)}>
              <ResponsiveImage
                src={productDetails.profileImages[0].fileUrl}
                alt={productDetails.profileImages[0].description}
                sizes="80px"
                roundedBorder={8}
              ></ResponsiveImage>
            </FoundryLink>
          </div>
          <div className="flex flex-col gap-3">
            <span>
              <FoundryLink
                href={Routes.shop.product(productDetails.slug)}
                className="color-blue-hover"
              >
                {productDetails.name}
              </FoundryLink>
            </span>
            {quantitySelector}
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <span
            className={`${
              discountCents ? "line-through opacity-50 ml-4" : "ml-4"
            }`}
          >
            {formatMoneyShort("USD", price)}
          </span>
          {!!discountCents && (
            <span className="ml-4 color-green">
              {formatMoneyShort("USD", price - discountCents)}
            </span>
          )}
          {!disableEdits && (
            <div
              className="w-6 h-6"
              onClick={() => handleRemoveProduct(product)}
            >
              <TrashEmptySvg className="cursor-pointer color-blue-hover" />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
